<template>
  <v-container>
    <avs-backButton
      v-if="!inDialog"
      backTo="NonConformities"
    />
    <AvsSteppers
      :value="tab"
      @change="v => (tab = v)"
    >
      <template v-slot:headers>
        <AvsStep
          step="1"
          :rules="[() => isFormValid.informations]"
        >
          {{ $t("commons.informations") }}
          <small v-show="!isFormValid.informations">{{ $t("commons.validation_issues") }}</small>
        </AvsStep>
        <v-divider></v-divider>
        <AvsStep
          step="2"
          :rules="[() => isFormValid.pictures]"
        >
          {{ $tc("commons.picture", 2) }}
          <small v-show="!isFormValid.pictures">{{ $t("commons.validation_issues") }}</small>
        </AvsStep>
        <v-divider></v-divider>
        <AvsStep
          step="3"
          :rules="[() => isFormValid.causeAndResolution]"
        >
          {{ $t("commons.cause") }}
          <small v-show="!isFormValid.causeAndResolution">
            {{ $t("commons.validation_issues") }}
          </small>
        </AvsStep>
        <v-divider></v-divider>
        <AvsStep step="4">
          {{ $t("commons.resolution") }}
        </AvsStep>
        <v-divider></v-divider>
        <AvsStep step="5">{{ $tc("commons.signature", 2) }}</AvsStep>
      </template>
      <template v-slot:items>
        <v-stepper-content step="1">
          <v-form
            ref="form-informations"
            v-model="isFormValid.informations"
            :readonly="isReadOnly"
            class="pt-2"
            lazy-validation
          >
            <InspectionInput
              v-model="nonConformity.inspection"
              :clearable="!isReadOnly"
              :disabled="!!inspection"
              :readonly="!!inspection"
              :rules="rules.inspection"
              dense
              outlined
            ></InspectionInput>

            <v-text-field
              v-model="nonConformity.nc_number"
              :rules="isDisable ? [] : rules.nc_number"
              :disabled="isDisable"
              auto-grow
              clearable
              dense
              outlined
              :label="$t('commons.nc_number')"
            />

            <v-textarea
              v-model="nonConformity.quick_description"
              :rules="rules.quick_description"
              :disabled="isDisable"
              clearable
              dense
              outlined
              :label="$t('commons.description')"
              rows="5"
            />

            <v-combobox
              v-model="nonConformity.area"
              :items="defaultAreaItems"
              :rules="rules.area"
              :disabled="isDisable"
              clearable
              dense
              outlined
              :label="$t('categories.nonconformity.area')"
            >
              <template #item="{ item }">
                <v-list-item-content>{{ item }}</v-list-item-content>
                <avs-deleteButton
                  @click.stop="deleteItem('area', item)"
                  :shape="{ text: true, small: true }"
                  :spantext="$t('categories.nonconformity.remove_list')"
                />
              </template>
            </v-combobox>

            <v-combobox
              v-model="nonConformity.event"
              :items="defaultEventItems"
              :rules="rules.event"
              :disabled="isDisable"
              clearable
              outlined
              dense
              :label="$t('categories.nonconformity.event')"
            >
              <template #item="{ item }">
                <v-list-item-content>{{ item }}</v-list-item-content>
                <avs-deleteButton
                  @click.stop="deleteItem('event', item)"
                  :shape="{ text: true, small: true }"
                  :spantext="$t('categories.nonconformity.remove_list')"
                />
              </template>
            </v-combobox>

            <v-combobox
              v-model="nonConformity.other"
              :items="defaultOtherItems"
              :rules="rules.other"
              :disabled="isDisable"
              clearable
              outlined
              dense
              :label="$t('categories.nonconformity.other')"
            >
              <template #item="{ item }">
                <v-list-item-content>{{ item }}</v-list-item-content>
                <avs-deleteButton
                  @click.stop="deleteItem('other', item)"
                  :shape="{ text: true, small: true }"
                  :spantext="$t('categories.nonconformity.remove_list')"
                />
              </template>
            </v-combobox>

            <!-- <DateInput
              v-model="nonConformity.report_date"
              :rules="rules.report_date"
              :max="maxReportDate"
              :min="minReportDate"
              :readonly="!!isReadOnly"
              dense
              :label="$t('commons.date.report_date')"
            ></DateInput> -->

            <DatetimePicker
              v-model="nonConformity.report_date"
              :min="minReportDate"
              :max="maxReportDate"
              :readonly="!!isReadOnly"
              :disabled="isDisable"
              :label="$t('commons.date.report_date')"
              :textFieldProps="{
                placeholder: $t('commons.date.report_date'),
                rules: rules.report_date
              }"
            />

            <v-select
              v-model="nonConformity.status"
              :items="statusItems"
              :rules="rules.status"
              dense
              outlined
              :label="$t('commons.status.name')"
            ></v-select>

            <v-select
              v-model="nonConformity.criticality"
              :items="criticalityItems"
              :rules="rules.status"
              :disabled="isDisable"
              dense
              outlined
              :label="$t('commons.criticality')"
            ></v-select>
          </v-form>
        </v-stepper-content>

        <v-stepper-content step="2">
          <v-col
            cols="12"
            class="pt-0 d-flex justify-center"
          >
            <NonConformityPictures
              :validate-counter="validateRequest.pictures"
              @validated="v => (isFormValid.pictures = v)"
              v-model="nonConformity.pictures"
              :rules="rules.pictures"
              :inspection="nonConformity.inspection"
              :disabled="isReadOnly"
              :isDisable="isDisable"
            />
          </v-col>
        </v-stepper-content>

        <v-stepper-content step="3">
          <v-form
            ref="form-cause-and-resolution"
            v-model="isFormValid.causeAndResolution"
            class="pt-2"
            lazy-validation
          >
            <v-text-field
              v-model="nonConformity.due_to"
              :rules="rules.due_to"
              :disabled="isDisable"
              auto-grow
              clearable
              dense
              outlined
              :label="$t('categories.nonconformity.due_to')"
            />

            <v-textarea
              v-model="nonConformity.detailed_description"
              :rules="rules.detailed_description"
              :disabled="isDisable"
              auto-grow
              clearable
              dense
              outlined
              :label="$t('categories.nonconformity.details_cause')"
              rows="4"
            />

            <v-textarea
              v-model="nonConformity.root_cause_analysis"
              :rules="rules.root_cause_analysis"
              :disabled="isDisable"
              auto-grow
              clearable
              dense
              outlined
              :label="$t('categories.nonconformity.rca')"
              rows="4"
            />
          </v-form>
        </v-stepper-content>

        <v-stepper-content step="4">
          <v-layout
            class="d-flex justify-center"
            v-if="loading"
          >
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-layout>
          <template v-else>
            <NonConformityActionList
              :nonConformity="nonConformity"
              :projectId="projectId"
              v-if="nonConformityExists"
            />
          </template>
        </v-stepper-content>

        <v-stepper-content step="5">
          <NonConformitySign
            :nonConformity="nonConformity"
            :signatures="signatures"
            @success="(v, m) => handleSuccess(v, 5, loadSignForm, m)"
            @error="handleError"
            @disableParent="handleSign"
          />
        </v-stepper-content>
      </template>
      <v-spacer></v-spacer>
      <template v-slot:actions>
        <avs-stepButton
          class="mx-2"
          icon="mdi-chevron-left"
          @click="tab--"
          :disabled="tab <= 1"
          :name="$t('commons.previous')"
        />
        <avs-stepButton
          class="mx-2"
          icon="mdi-chevron-right"
          right
          @click="tabIncrement"
          :name="nextLabel"
        />
      </template>
    </AvsSteppers>

    <avs-CloseDialog
      v-model="closeDialogIsOpen"
      @cancel="handleCancel"
      @confirm="handleConfirm"
    />
  </v-container>
</template>

<script>
import _ from "lodash"
const noop = () => undefined
import { NonConformityService, PictureService, ProjectService } from "@/services"
import {
  NON_CONFORMITY_AREAS,
  STATUS_ITEMS,
  STATUS_ITEMS_DISABLED,
  CRITICALITY_ITEMS
} from "@/utils/consts"
import { mkNonConformity } from "@/utils/datamodels"

import { stringToDate } from "@/utils/datetime"
import { isInspector } from "@/utils/middleware"

import {
  // DateInput,
  InspectionInput,
  NonConformityPictures,
  NonConformityActionList
} from "@/components"
import { required, validateLength } from "@/utils/validator"

export default {
  name: "NonConformityCreateUpdate",
  components: {
    AvsStep: () => import("@/components/generics/general/AvsStep.vue"),
    AvsSteppers: () => import("@/components/generics/general/AvsSteppers.vue"),
    DatetimePicker: () => import("@/components/inputs/DatetimePicker.vue"),
    NonConformitySign: () => import("@/components/ri3/non-conformity/NonConformitySign.vue"),
    NonConformityPictures,
    NonConformityActionList,
    // DateInput,
    InspectionInput
  },
  props: {
    inDialog: {
      type: Boolean,
      default: false
    },
    inDialogParams: {
      type: Object,
      default: () => {}
    }
  },
  data: function () {
    return {
      NON_CONFORMITY_AREAS,
      nonConformityRef: null,
      nonConformity: mkNonConformity(),
      tab: this.$route.params.tab ? this.$route.params.tab : 1,
      nextLabel: this.$t("commons.next"), //NEXT.DEFAULT,
      loading: false,
      step: -1,
      minReportDate: null,
      maxReportDate: stringToDate(),
      rules: {
        inspection: [required],
        nc_number: [required],
        quick_description: [],
        area: [required],
        event: [required],
        other: [],
        report_date: [required],
        status: [required],
        due_to: [],
        detailed_description: [],
        root_cause_analysis: [validateLength],
        corrective_action: [],
        preventive_action: [],
        pictures: []
      },
      isFormValid: {
        informations: false,
        pictures: false,
        causeAndResolution: false
      },
      validateRequest: {
        pictures: 0
      },
      areasData: {},
      signatures: undefined,
      isDisable: false,
      closeDialogIsOpen: false
    }
  },
  created() {
    this.fetchNonConformity()
    this.fetchAreas()
  },
  methods: {
    deleteItem(type, value) {
      const params = { type, value }
      ProjectService.areas.delete(this, this.projectId, params).then(response => {
        this.fetchAreas()
      })
    },
    save() {
      this.$refs["form-informations"].validate()
      this.validateRequest.pictures++
      this.$refs["form-cause-and-resolution"].validate()
      this.$nextTick(() => {
        this.nonConformity.lang = this.$i18n.locale
        if (
          this.isFormValid.informations &&
          this.isFormValid.pictures &&
          this.isFormValid.causeAndResolution
        )
          if (this.nonConformityExists) {
            this.updateNonConformity()
          } else {
            this.createNonConformity()
          }
        else {
          this.tab = 1
          const message = this.$t("messages.warning.all_required_info")
          this.send(message, "warning")
        }
      })
    },
    tabIncrement() {
      return this.tab < 5 ? this.tab++ : this.$router.push({ name: "NonConformities" })
    },
    send(msg, color) {
      this.$store.commit("app/message", { msg, color })
    },
    getNonConformity(id) {
      if (id) {
        let t = this
        NonConformityService.get(this, this.projectId, id).then(r => {
          t.nonConformity = r
          t.nonConformityRef = _.cloneDeep(t.nonConformity)
          if (t.nonConformity.state === 4) {
            t.isDisable = true
          }
        })
      }
    },
    createNonConformity() {
      let tmp = _.cloneDeep(this.nonConformity)

      // We rename "file_url" to "file" for the create to work
      tmp.pictures = tmp.pictures.map(picture => {
        picture.file = picture.file_url
        delete picture.file_url
        return picture
      })

      this.loading = true
      NonConformityService.create(this, this.projectId, this.nonConformity.inspection.id, tmp)
        .then(response => {
          this.nonConformity = response
          this.nonConformityRef = _.cloneDeep(response)
          this.send(
            this.$t("messages.success.saved", {
              entity: this.$tc("categories.nonconformity.name", 1)
            }),
            "success"
          )
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            // alert("Access denied: You do not have permission to access this resource.")
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    updateNonConformity() {
      const requests = []
      const picturesToCreate = []
      //  Create pictures that has no id
      //  Update pictures that were edited (has id)
      //  Delete pictures (has deleted==true)
      this.nonConformity.pictures.map(p => {
        if (p.id) {
          if (p.removed) {
            requests.push(PictureService.remove(this, p.id))
          } else {
            const refPicture = this.nonConformityRef.pictures.find(refP => refP.id === p.id)
            if (p.unassigned === true) {
              let obj = {
                non_conformity: this.nonConformity.id,
                name: p.name
              }
              if (p.file_url.startsWith("data")) {
                obj["file"] = p.file_url
              }
              requests.push(PictureService.update(this, p.id, obj))
            } else if (
              !_.isEqual(_.pick(p, ["name", "file_url"]), _.pick(refPicture, ["name", "file_url"]))
            ) {
              let updatedPic = _.pick(p, ["name", "file_url"])
              if (updatedPic.file_url != refPicture.file_url) {
                updatedPic["file"] = updatedPic["file_url"]
                delete updatedPic["file_url"]
              }
              requests.push(PictureService.update(this, p.id, updatedPic))
            }
          }
        } else {
          picturesToCreate.push(_.pick(p, ["name", "file_url"]))
        }
      })
      if (picturesToCreate.length > 0) {
        let mappedPictures
        mappedPictures = picturesToCreate.map(p => {
          return {
            name: p.name,
            file: p.file_url
          }
        })
        requests.push(
          PictureService.create(this, this.nonConformity.id, {
            files: mappedPictures
          })
        )
      }
      if (
        !_.isEqual(
          _.omit(this.nonConformity, ["pictures"]),
          _.omit(this.nonConformityRef, ["pictures"])
        )
      ) {
        requests.push(
          NonConformityService.update(
            this,
            this.nonConformity.id,
            _.omit(this.nonConformity, ["pictures"])
          )
        )
      }
      if (requests.length > 0) {
        this.loading = true
        Promise.all(requests)
          .then(() => {
            this.send(
              this.$t("messages.success.updated", {
                entity: this.$tc("categories.nonconformity.name", 1)
              }),
              "success"
            )
          })
          .catch(err => {
            let message = ""
            for (const e in err.response.data) {
              message += "- " + e + " : " + err.response.data[e] + "\n"
            }
            this.send(message, "error")
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        this.send(this.$t("messages.success.no_updated"), "info")
      }
    },
    fetchNonConformity() {
      // Load nonconformity
      this.getNonConformity(this.id)
    },
    fetchAreas() {
      // Load project/company areas
      ProjectService.areas.get(this, this.projectId).then(data => {
        this.areasData = data
      })
    },
    handleError(msg) {
      // Handle message display
      this.$store.commit("app/message", { msg, color: "error" })
    },
    handleSign() {
      // Handle message display
      this.isDisable = true
      this.closeDialogIsOpen = true
    },
    handleSuccess(isSaved, nextTab, postProcess = noop, message = null) {
      // Handle message display
      const msg_success = message ? message : this.$t("messages.success.operation")
      const data = isSaved
        ? { msg: msg_success, color: "success" }
        : { msg: this.$t("messages.success.no_updated"), color: "info" }
      this.$store.commit("app/message", data)

      // Handle next display
      this.tab = nextTab

      // Postprocess
      postProcess()
    },
    handleConfirm() {
      NonConformityService.close(this, this.nonConformity.id)
        .then(r => {
          this.$store.commit("app/message", {
            msg: this.$t("messages.success.closed", {
              entity: this.$tc("categories.nonconformity.name", 1)
            }),
            color: "success"
          })
          this.closeDialogIsOpen = false
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            // alert("Access denied: You do not have permission to access this resource.")
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
      this.closeDialogIsOpen = false
    },
    handleCancel() {
      this.closeDialogIsOpen = false
    },
    async fetchNonConformitySignatures() {
      this.signatures = await NonConformityService.getSignatures(this.nonConformity.id)
    },
    async loadSignForm() {
      if (this.nonConformityExists) {
        this.loading = true
        this.fetchNonConformitySignatures().finally(() => {
          this.loading = false
        })
      }
    }
  },
  watch: {
    tab(newValue, oldValue) {
      if (oldValue === 1 && newValue === 2) {
        this.tab = this.$refs["form-informations"].validate() ? 2 : 1
      } else if (oldValue === 2 && newValue === 3) {
        this.tab = this.isFormValid.pictures ? 3 : 2
        this.validateRequest.pictures++
      } else if (oldValue === 3 && newValue === 4) {
        this.save()
      }

      // Set Save label
      if (newValue === 3) {
        this.nextLabel = this.$t("commons.save_next") //NEXT.SAVE
      } else if (newValue === 5) {
        this.nextLabel = this.$t("commons.finish") //NEXT.FINISH
      } else if (newValue === 4) {
        this.nextLabel = this.$t("commons.next")
      } else {
        this.loadSignForm()
        this.nextLabel = this.$t("commons.next") //NEXT.DEFAULT
      }
    },
    "nonConformity.inspection": {
      handler(newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          this.minReportDate = stringToDate(newValue.start_date)
        }
      },
      deep: true
    }
  },
  computed: {
    defaultAreaItems() {
      return Object.keys(this.areasData)
    },
    defaultEventItems() {
      const eventsData = this.areasData[this.nonConformity.area] ?? {}
      return Object.keys(eventsData)
    },
    defaultOtherItems() {
      const eventsData = this.areasData[this.nonConformity.area] ?? {}
      return eventsData[this.nonConformity.event] ?? []
    },
    statusItems() {
      return isInspector(this.projectRole) ? STATUS_ITEMS : STATUS_ITEMS_DISABLED
    },
    criticalityItems() {
      return isInspector(this.projectRole) ? CRITICALITY_ITEMS : CRITICALITY_ITEMS
    },
    id() {
      return this.inDialog ? this.inDialogParams?.id : this.$route.params.id || null
    },
    inspection() {
      return this.$route.query.inspection || this.$route.params.id || null
    },
    // A non-conformity is read-only if its closed_date is null for operators
    isReadOnly() {
      return !!this.nonConformity.closed_at && !isInspector(this.projectRole)
    },
    nonConformityExists() {
      return this.nonConformity.id > 0
    },
    projectId() {
      return this.$store.state.app.project.id
    },
    projectRole() {
      return this.$store.state.app.project.role
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-container {
  display: flex;
  flex-direction: column;
  height: 80vh;
}
.stepper-item {
  flex: 1 1 auto;
  overflow-y: auto;
}
</style>
